import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from './AuthContext';
import BookCallModal from '../components/booking/BookCallModal';
import {
  LANDING_BOOK_URL,
  isKalendarBookingUrl,
  rememberBookingCta,
  trackBookCallClick,
  withBookingCta,
} from '../lib/bookingCta';

/**
 * One "talk to a human" for the whole app: the Kalendar booking dialog.
 *
 * Every "Talk to us" / "Contact us" / "Message us" used to open the Intercom
 * messenger, one call site at a time, while "Book a call" opened Kalendar.
 * Founder call (2026-10-15): talking to us means booking a call, everywhere.
 * So the opener lives here, once — a screen calls `useBookCall()(cta)` and
 * never decides for itself what "contact" means.
 *
 * Same journey as the waitlist wall and /plans (lib/bookingCta.ts):
 * book_call_click + the CTA cookie on click, the ops-set Kalendar page
 * (AccessSettings.salesBookingUrl) in BookCallModal over whatever page is
 * open, and the server-side `booking_completed` record on a booking. A
 * booking page that isn't Kalendar's runs none of our code, so it opens in a
 * new tab with the CTA on the link; no page set at all falls back to the
 * landing's /go/book, which embeds the same calendar. Never the messenger.
 *
 * The URL is fetched once per signed-in session, ahead of any click, so the
 * click can open its tab synchronously — a window.open after an await is a
 * popup the browser blocks. A click that still beats the fetch waits for it.
 */
export type BookCall = (cta: string) => void;

const BookCallContext = createContext<BookCall | null>(null);

export const BookCallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState<{ url: string; cta: string } | null>(null);
  const urlRequest = useRef<Promise<string> | null>(null);
  const resolvedUrl = useRef<string | null>(null);

  const fetchBookingUrl = useCallback((): Promise<string> => {
    if (!urlRequest.current) {
      urlRequest.current = authAPI
        .waitlistInfo()
        .then((res) => {
          resolvedUrl.current = res?.data?.salesBookingUrl || '';
          return resolvedUrl.current;
        })
        .catch(() => {
          urlRequest.current = null; // a failed fetch is retried on the next click
          return '';
        });
    }
    return urlRequest.current;
  }, []);

  const signedIn = !!user;
  useEffect(() => {
    if (signedIn) void fetchBookingUrl();
  }, [signedIn, fetchBookingUrl]);

  const bookCall = useCallback<BookCall>(
    (cta) => {
      trackBookCallClick(cta);
      rememberBookingCta(cta);
      const go = (url: string) => {
        if (url && isKalendarBookingUrl(url)) setOpen({ url, cta });
        else openBookingTab(url, cta);
      };
      if (resolvedUrl.current !== null) go(resolvedUrl.current);
      else void fetchBookingUrl().then(go);
    },
    [fetchBookingUrl],
  );

  return (
    <BookCallContext.Provider value={bookCall}>
      {children}
      {open && (
        <BookCallModal
          bookingUrl={open.url}
          cta={open.cta}
          user={user}
          onClose={() => setOpen(null)}
          // The server-side record (PostHog + Customer.io, app workspace).
          onBooked={({ eventType }) => {
            authAPI.bookingCompleted({ cta_source: open.cta, event_type: eventType }).catch(() => {});
          }}
        />
      )}
    </BookCallContext.Provider>
  );
};

/** Opens the booking dialog, labelled with the CTA that asked for it. */
export function useBookCall(): BookCall {
  return useContext(BookCallContext) ?? bookCallWithoutProvider;
}

function openBookingTab(url: string, cta: string): void {
  window.open(withBookingCta(url || LANDING_BOOK_URL, cta), '_blank', 'noopener,noreferrer');
}

// Outside the provider (a component rendered on its own, as in its tests):
// the landing's booking page, still labelled. Never a dead button.
const bookCallWithoutProvider: BookCall = (cta) => {
  trackBookCallClick(cta);
  rememberBookingCta(cta);
  openBookingTab('', cta);
};
